import HTMLTemplateHelper from '../../helpers/html-template-helper';
import MessageHelper from '../../helpers/message-helper';
import SalesOrderData from '../../model/sales-order-data';
import OrderSearchForm from '../../components/orders/order-search-form';
import Table from '../../components/shared/table';
import SalesOrderEntryData from '../../model/sales-order-entry-data';
import { formatNumber } from '../../helpers/utils';
import AbstractAsyncDetailTablePage from '../abstract-async-detail-table-page';
import { BatchFetcher } from '../../helpers/batch-fetcher';
import DeliveryAvailability from '../../components/product/delivery-availability';
import StockAndPriceData from '../../model/stock-and-price-data';

interface SalesOrderEntryDataExtended extends SalesOrderEntryData {
  orderId: string;
  salesDocumentType: string;
  requestedDeliveryDate: number;
  stockAndPrice?: StockAndPriceData;
}

export default class OrderedItemsPage extends AbstractAsyncDetailTablePage<SalesOrderData> {
  private readonly ICON_ALERT = `<img class="icon -small"
    src="${window.ACC.config.commonResourcePath}/images/icons/alert-round-orange-bold.png" alt=""/>`;

  protected readonly searchForm: OrderSearchForm;
  protected readonly table: Table;
  protected readonly tableColumnDefinitions: DataTables.ColumnDefsSettings[] = [
    {
      className: 'js-showDetails',
      targets: 0,
      orderable: false,
      searchable: false,
      data: null,
      defaultContent: '<i class="table__toggle-child fas fa-chevron-right"></i>',
    },
    {
      targets: 1,
      className: 'cursor-pointer js-showDetails',
      data: (data: SalesOrderData): string => `<a>${data.orderId}</a>`, // eslint-disable-line max-len
    },
    { targets: 2, data: 'externalId' },
    {
      targets: 3,
      data: {
        _: 'orderDate',
        sort: 'voucherDate',
      },
    },
    { targets: 4, data: 'orderTime' },
    { targets: 5, data: 'status' },
    { targets: 6, data: 'salesDocumentType' },
    { targets: 7, data: 'orderSource' },
    { targets: 8, data: (data: SalesOrderData): string => formatNumber(data.netAmount, 2), className: 'text-align-right' },
    { targets: 9, data: (data: SalesOrderData): string => formatNumber(data.grossAmount, 2), className: 'text-bold text-align-right' },
    {
      targets: 10,
      data: (data: SalesOrderData): string => (data.salesDocumentType === 'ZWKR' || data.salesDocumentType === 'ZJIB'
        ? data.requestedDeliveryWeek : ''),
      className: 'text-align-right',
    },
    {
      targets: 11,
      className: 'text-align-center',
      data: (data: SalesOrderData): string => HTMLTemplateHelper.createLinkButton({
        href: `${window.ACC.config.encodedContextPath}/orders/orderedItems/print?token=${data.fileDownloadJwt}`,
        buttonClass: '-clear -compact',
        text: MessageHelper.getMessage('common.print'),
        textClass: 'sr-only',
        icon: 'fas fa-file-download',
      }).outerHTML,
    },
  ];

  protected readonly detailTableTemplate: JQuery<HTMLTemplateElement>;
  protected readonly detailTableColumnDefinitions: DataTables.ColumnDefsSettings[] = [
    { targets: 0, data: 'id', width: '5%' },
    { targets: 1, data: 'reference', width: '10%' },
    { targets: 2, data: 'materialNumber', width: '15%' },
    { targets: 3, data: 'materialDescription', width: '15%' },
    { targets: 4, data: 'type', width: '5%' },
    {
      targets: 5, data: 'orderQuantity', width: '5%', className: 'text-align-right',
    },
    {
      targets: 6, data: 'deliveredQuantity', width: '5%', className: 'text-align-right',
    },
    {
      targets: 7,
      className: 'backorder-availability-data-cell',
      orderable: false,
      width: '20%',
      data: (data: SalesOrderEntryDataExtended): SalesOrderEntryDataExtended | null => {
        if (data.eligibleForMasterClassData && !data.rejectionReason) {
          if (!data.stockAndPrice) {
            return null;
          }
        }
        return data;
      },
      render: (data: SalesOrderEntryDataExtended | null): string => {
        if (!data) {
          return '<span class="loading-indicator"></span>';
        }
        if (data.rejectionReason) {
          return `<div class="rejected-reason">${this.ICON_ALERT}<span>${data.deliveryInfo}</span></div>`;
        }
        if (data.eligibleForMasterClassData && data.deliveryStatusCode !== '002') {
          return `<div
            data-code="${data.materialNumber}"
            ${OrderedItemsPage.displayWeekForWKROrder(data) ? 'data-date-format="week"' : ''}
            data-estimated-delivery-date-formatted="${data.estimatedDeliveryDateFormatted}"
            data-render-type="backorder"
            data-variant="no-icon"
            class="delivery-dates"
          ></div>`;
        }
        return data.deliveryInfo;
      },
      createdCell: (cell: Node, cellData: SalesOrderEntryDataExtended): void => {
        if (cellData && cellData.eligibleForMasterClassData && cellData.deliveryStatusCode !== '002') {
          const container = (cell as HTMLElement).children[0] as HTMLDivElement;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const instance: DeliveryAvailability = new DeliveryAvailability(container, cellData.stockAndPrice?.stockForecasts);
        }
      },
    },
    { targets: 8, data: 'salesDocumentId', width: '10%' },
    {
      targets: 9, data: (data: SalesOrderEntryData): string => formatNumber(data.netValue, 2), className: 'text-align-right', width: '10%',
    },
  ];

  constructor(body: JQuery) {
    super();
    this.searchForm = new OrderSearchForm(body.find<HTMLFormElement>('.js-orderedItemsForm'));
    this.detailTableTemplate = body.find<HTMLTemplateElement>('#js-orderDetailsTemplate');
    this.table = new Table(body.find('.js-table'), this.tableColumnDefinitions, { drawCallback: this.generateDrawCallback('orderId') });
    this.bindAsyncData();
    this.bindShowDetails();
  }

  static displayWeekForWKROrder(data: SalesOrderEntryDataExtended): boolean {
    const isWKR = data.salesDocumentType === 'ZWKR' || data.salesDocumentType === 'ZJIB';
    return isWKR && data.requestedDeliveryDate > new Date().getTime();
  }

  // eslint-disable-next-line class-methods-use-this
  protected getDetailUrl(salesOrder: SalesOrderData): string {
    return `${window.ACC.config.encodedContextPath}/orders/orderedItems/details/${salesOrder.orderId}`;
  }

  protected createDetailTable(container: JQuery, rowData: SalesOrderData): Table {
    const detailTable = Table.init(container, this.detailTableColumnDefinitions);

    const detailUrl = this.getDetailUrl(rowData);
    void $.ajax({
      url: detailUrl,
      success: (data: SalesOrderData) => OrderedItemsPage.setDetailRows(data, detailTable),
    });

    return detailTable;
  }

  static setDetailRows(data: SalesOrderData, detailTable: Table): void {
    const mappedData: SalesOrderEntryDataExtended[] = data.entries.map((entry: SalesOrderEntryData) => ({
      orderId: data.orderId,
      salesDocumentType: data.salesDocumentType,
      requestedDeliveryDate: data.requestedDeliveryDate,
      ...entry,
    }));
    detailTable.setRows(mappedData);

    const stockRequestData = mappedData
      .filter((entry) => entry.eligibleForMasterClassData)
      .map((entry) => ({
        productCode: entry.materialNumber,
        quantity: entry.orderQuantity,
        orderNumber: entry.orderId,
        orderPosition: entry.id,
        calendarWeeks: OrderedItemsPage.displayWeekForWKROrder(entry),
      }));

    const endpoint = `${window.ACC.config.encodedContextPath}/products/masterStock`;
    const fetcher = new BatchFetcher<typeof stockRequestData[number], StockAndPriceData>(() => endpoint);

    void fetcher.fetchChunked(
      stockRequestData,
      (response) => {
        response.forEach((stockAndPrice) => {
          mappedData
            .filter((entry) => entry.orderId === stockAndPrice.orderNumber && entry.id === stockAndPrice.orderPosition)
            .forEach((entry) => {
              /* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["entry"] }] */
              entry.stockAndPrice = stockAndPrice;
            });
        });

        // Clear and re-add rows to get them to render properly
        detailTable.getInstance()
          .clear()
          .rows.add(mappedData)
          .draw();
      },
    );
  }
}
